import React from 'react'
import type { TFunction } from 'i18next'
import { MetricCard } from '../../../atoms/MetricCard'
import { TooltipMetricExplanation } from '../../../TooltipMetricExplanation'
import type { ElectricityAnalysis } from '@popety-io/types'
import { formatPrice, formatCHF, getPriceRating } from '../electricityService'
import { GeographicComparisonCard } from '../cards/GeographicComparisonCard'
import { GridOperatorComparisonCard } from '../cards/GridOperatorComparisonCard'
import { InsightCard } from '../cards/InsightCard'
import { TabContentContainer, MetricsGrid } from '../ElectricitySection.styles'

export interface OverviewTabProps {
  data: ElectricityAnalysis | null
  t: TFunction
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ data, t }) => {
  if (!data) return null

  const { current_data, annual_cost, price_comparison } = data

  // Determine price rating from comparison
  const rating = price_comparison
    ? getPriceRating(
        price_comparison.vs_canton_median,
        price_comparison.vs_national_median
      )
    : 'medium'

  return (
    <TabContentContainer>
      {/* Key Metrics */}
      <MetricsGrid>
        <MetricCard
          title={t('metrics.cheapestPrice.title')}
          value={formatPrice(
            current_data.total_price_min,
            t('gridOperators.unit')
          )}
          subtitle={
            current_data.grid_operator_name_min ||
            t('metrics.cheapestPrice.subtitle')
          }
          variant="default"
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.cheapestPrice.title')}
              shortDescription={t('tooltips.cheapestPrice.shortDescription')}
              calculation={t('tooltips.cheapestPrice.calculation')}
              importance={t('tooltips.cheapestPrice.importance')}
              limitations={t('tooltips.cheapestPrice.limitations')}
              source={t('tooltips.cheapestPrice.source')}
            />
          }
        />

        <MetricCard
          title={t('metrics.priceRange.title')}
          value={`${current_data.total_price_min.toFixed(2)} – ${current_data.total_price_max.toFixed(2)} ${t('gridOperators.unit')}`}
          subtitle={t('metrics.priceRange.subtitle')}
          variant="default"
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.priceRange.title')}
              shortDescription={t('tooltips.priceRange.shortDescription')}
              calculation={t('tooltips.priceRange.calculation')}
              importance={t('tooltips.priceRange.importance')}
              limitations={t('tooltips.priceRange.limitations')}
              source={t('tooltips.priceRange.source')}
            />
          }
        />

        <MetricCard
          title={t('metrics.annualCost.title')}
          value={formatCHF(annual_cost.cost_min)}
          subtitle={t('metrics.annualCost.subtitle')}
          variant="default"
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.annualCost.title')}
              shortDescription={t('tooltips.annualCost.shortDescription')}
              calculation={t('tooltips.annualCost.calculation')}
              importance={t('tooltips.annualCost.importance')}
              limitations={t('tooltips.annualCost.limitations')}
              source={t('tooltips.annualCost.source')}
            />
          }
        />
      </MetricsGrid>

      {/* Geographic Comparison */}
      {price_comparison && <GeographicComparisonCard data={data} t={t} />}

      {/* Grid Operator Comparison Table */}
      {current_data.grid_operators &&
        current_data.grid_operators.length > 0 && (
          <GridOperatorComparisonCard
            gridOperators={current_data.grid_operators}
            t={t}
          />
        )}

      {/* Insight Card */}
      <InsightCard rating={rating} t={t} />
    </TabContentContainer>
  )
}
